import {Component, computed, inject, OnInit, signal, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {TranslatePipe} from '@ngx-translate/core';
import {PublicProfileService} from '@services/user/public-profile.service';
import {MarketplaceService} from '@services/card-game/marketplace.service';
import {AuthService} from '@services/auth.service';
import {PublicProfile} from '@/app/core/models/public-profile.model';
import {ProfileCompare} from '@/app/core/models/profile-compare.model';
import {CardListing} from '@/app/core/models/marketplace.model';
import {LocalizedDatePipe, TooltipDirective} from '@/app/shared';
import {CardComponent} from '@/app/shared/components/card/card.component';
import {rankIcon} from '@/app/core/utils/rankUtils';
import {NgClass} from '@angular/common';
import {TranslateDynamicService} from '@services/translate-dynamic.service';
import {catchError, of} from 'rxjs';

const PREDICTION_STATUS_KEYS: Record<string, string> = {
	PENDING: 'page.public-profile.prediction-status.pending',
	WON: 'page.public-profile.prediction-status.won',
	LOST: 'page.public-profile.prediction-status.lost',
};

/** Fenêtre de fraîcheur pour le badge "vu récemment en live" — cf. lastSeenLiveAt. */
const RECENTLY_LIVE_WINDOW_DAYS = 7;

@Component({
	selector: 'peps-public-profile',
	imports: [TranslatePipe, CardComponent, LocalizedDatePipe, NgClass, TooltipDirective],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './public-profile.component.html',
})
export class PublicProfileComponent implements OnInit {

	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly publicProfileService = inject(PublicProfileService);
	private readonly marketplaceService = inject(MarketplaceService);
	private readonly titleService = inject(Title);
	protected readonly authService = inject(AuthService);
	protected readonly translateDynamicService = inject(TranslateDynamicService);

	isLoading = signal(true);
	profile = signal<PublicProfile | null>(null);
	compare = signal<ProfileCompare | null>(null);
	/** Cartes actuellement mises en vente sur le marché par ce joueur. */
	listings = signal<CardListing[]>([]);

	/** Nombre max de listings affichés sur le profil public. */
	private readonly LISTINGS_LIMIT = 50;

	/** Tuiles KPI de la colonne latérale — chiffres synthétiques du joueur. */
	statTiles = computed<{ value: number; labelKey: string }[]>(() => {
		const p = this.profile();
		if (!p) return [];
		return [
			{ value: p.validatedQuestCount, labelKey: 'page.public-profile.stat-quests' },
			{ value: p.favoriteCards.length, labelKey: 'page.public-profile.stat-favorites' },
			{ value: this.listings().length, labelKey: 'page.public-profile.stat-listings' },
			{ value: p.badges.length, labelKey: 'page.public-profile.stat-badges' },
			{ value: p.boostersOpened, labelKey: 'page.public-profile.stat-boosters' },
		];
	});

	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			const username = params.get('username');
			if (!username) {
				this.router.navigate(['/not-found']);
				return;
			}
			this.loadProfile(username);
		});
	}

	rankIcon(code: string): string {
		return rankIcon(code);
	}

	predictionStatusKey(status: string): string {
		return PREDICTION_STATUS_KEYS[status] ?? PREDICTION_STATUS_KEYS['PENDING'];
	}

	predictionStatusClass(status: string): string {
		if (status === 'WON') return 'border-peps-gold text-peps-gold';
		if (status === 'LOST') return 'border-peps-red text-peps-red';
		return 'border-peps-dark-500 text-peps-dark-200/45';
	}

	/** true si le joueur a validé STREAM_PRESENCE il y a moins de RECENTLY_LIVE_WINDOW_DAYS jours. */
	isRecentlyLive(): boolean {
		const lastSeen = this.profile()?.lastSeenLiveAt;
		if (!lastSeen) return false;
		const elapsedMs = Date.now() - new Date(lastSeen).getTime();
		return elapsedMs <= RECENTLY_LIVE_WINDOW_DAYS * 24 * 60 * 60 * 1000;
	}

	betWinRate(stats: {total: number; won: number}): number {
		return stats.total > 0 ? Math.round((stats.won / stats.total) * 100) : 0;
	}

	private loadProfile(username: string): void {
		this.isLoading.set(true);
		this.compare.set(null);
		this.listings.set([]);
		this.publicProfileService.getPublicProfile(username).subscribe({
			next: (profile) => {
				this.profile.set(profile);
				this.titleService.setTitle('Team Peps - ' + profile.username);
				this.isLoading.set(false);
				this.loadCompareIfRelevant(username);
				this.loadListings(username);
			},
			error: () => {
				this.router.navigate(['/not-found']);
			},
		});
	}

	/** Listings actifs du joueur — échec silencieux (marché en maintenance, etc.). */
	private loadListings(username: string): void {
		this.marketplaceService.getListings({sellerUsername: username, size: this.LISTINGS_LIMIT}).pipe(
			catchError(() => of(null))
		).subscribe(page => this.listings.set(page?.content ?? []));
	}

	/** Comparatif face-à-face : uniquement si connecté et pas sur son propre profil. Échec silencieux (pas de session, saison sans stats...). */
	private loadCompareIfRelevant(username: string): void {
		if (!this.authService.isAuthenticated() || this.authService.getUser()?.username === username) {
			return;
		}
		this.publicProfileService.compareProfiles(username).pipe(
			catchError(() => of(null))
		).subscribe(compare => this.compare.set(compare));
	}
}
